import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    IconButton,
    Popover,
    MenuItem,
    Checkbox,
    Button,
    Chip,
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    CardGiftcard as CardGiftcardIcon,
    MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axios from '../config/axiosConfig';
import TitleAndInfo from './TitleAndInfo';

const PAGE_SIZE = 10;
const DEBOUNCE_MS = 400;

const TRANSACTION_TYPE_OPTIONS = [
    { key: 'Buy', label: 'Buy', colorKey: 'success', icon: <TrendingUpIcon sx={{ fontSize: '0.9rem' }} /> },
    { key: 'Sale', label: 'Sale', colorKey: 'error', icon: <TrendingDownIcon sx={{ fontSize: '0.9rem' }} /> },
    { key: 'Award', label: 'Award', colorKey: 'success', icon: null },
    { key: 'Option Exercise', label: 'Option Exercise', colorKey: 'info', icon: null },
    { key: 'Gift', label: 'Gift', colorKey: 'text', icon: <CardGiftcardIcon sx={{ fontSize: '0.9rem' }} /> },
    { key: 'Other', label: 'Other', colorKey: 'warning', icon: <MoreHorizIcon sx={{ fontSize: '0.9rem' }} /> },
];

const AD_OPTIONS = [
    { key: 'A', label: 'Acquired', color: '#1e7e34' },
    { key: 'D', label: 'Disposed', color: '#bd2130' },
    { key: 'I', label: 'Indirect', color: '#6c757d' },
];

// --- Filter Components ---

const TransactionTypeFilter = ({ selectedTypes, setSelectedTypes, theme }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleToggle = (key) =>
        setSelectedTypes((prev) =>
            prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
        );

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    borderRadius: 2, textTransform: 'none', fontSize: '0.875rem',
                    minWidth: 160, justifyContent: 'space-between',
                    color: 'text.primary', borderColor: 'divider', height: 40, px: 1.5,
                }}
            >
                {selectedTypes.length > 0 ? `Type (${selectedTypes.length})` : 'Transaction Type'}
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { mt: 0.5, borderRadius: 2, width: 220 } }}
            >
                {TRANSACTION_TYPE_OPTIONS.map((opt) => {
                    const color = opt.colorKey === 'text'
                        ? theme.palette.text.secondary
                        : theme.palette[opt.colorKey]?.main || theme.palette.text.primary;
                    return (
                        <MenuItem
                            key={opt.key}
                            onClick={() => handleToggle(opt.key)}
                            dense
                            sx={{ py: 0.25, px: 1, minHeight: 32 }}
                        >
                            <Checkbox
                                checked={selectedTypes.includes(opt.key)}
                                size="small"
                                sx={{ p: 0.25, mr: 0.75 }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {opt.icon && <Box sx={{ color, display: 'flex' }}>{opt.icon}</Box>}
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color, fontWeight: 600 }}>
                                    {opt.label}
                                </Typography>
                            </Box>
                        </MenuItem>
                    );
                })}
                {selectedTypes.length > 0 && (
                    <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => setSelectedTypes([])} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                            Clear all
                        </Button>
                    </Box>
                )}
            </Popover>
        </>
    );
};

const AcquisitionDispositionFilter = ({ selectedAD, setSelectedAD, theme }) => {
    const handleToggle = (key) =>
        setSelectedAD((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );

    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {AD_OPTIONS.map((opt) => {
                const active = selectedAD.includes(opt.key);
                return (
                    <Chip
                        key={opt.key}
                        label={opt.label}
                        size="small"
                        onClick={() => handleToggle(opt.key)}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                            height: 32, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                            backgroundColor: active ? `${opt.color}25` : 'transparent',
                            borderColor: active ? opt.color : 'divider',
                            color: active ? opt.color : 'text.secondary',
                            '&:hover': { backgroundColor: `${opt.color}15` },
                        }}
                    />
                );
            })}
        </Box>
    );
};

// --- Main Component ---

const InsiderTracker = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [page, setPage] = useState(0);
    const [totalRows, setTotalRows] = useState(0);

    // Filter state
    const [symbolQuery, setSymbolQuery] = useState('');
    const [debouncedSymbol, setDebouncedSymbol] = useState('');
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedAD, setSelectedAD] = useState([]);

    // Cache state
    const [cachedData, setCachedData] = useState({});
    const cachedDataRef = useRef(cachedData);
    cachedDataRef.current = cachedData;

    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const debounceTimer = useRef(null);

    const titleTop = "Insider Trading Activity";
    const title = "";
    const info = "Track insider trading activities of corporate executives, directors, and major shareholders. View detailed information about stock purchases, sales, and transaction amounts.";

    // Build a cache key from all active filters
    const getCacheKey = useCallback((symbol, types, ad) => {
        return `${symbol}|${types.join(',')}|${ad.join(',')}`;
    }, []);

    // Debounce search input
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSymbol(symbolQuery);
            setPage(0);
        }, DEBOUNCE_MS);
        return () => clearTimeout(debounceTimer.current);
    }, [symbolQuery]);

    // Reset page when non-debounced filters change
    useEffect(() => {
        setPage(0);
    }, [selectedTypes, selectedAD]);

    const fetchTradesData = useCallback(async (page, symbol, types, ad) => {
        const cacheKey = getCacheKey(symbol, types, ad);
        const pageCache = cachedDataRef.current[cacheKey] || {};
        if (pageCache[page] !== undefined) {
            setTotalRows(pageCache._total ?? 0);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const params = {
                page: page,
                limit: PAGE_SIZE,
                symbol: symbol || undefined,
                transactionType: types.length > 0 ? types.join(',') : undefined,
                acquisitionOrDisposition: ad.length > 0 ? ad.join(',') : undefined,
                showAllTypes: types.length > 0 ? 'true' : undefined,
            };

            const response = await axios.get('/api/insiderTrading', { params });
            const { data: responseData, pagination } = response.data;
            const total = pagination?.total || 0;

            const transformedData = responseData.map(trade => ({
                id: trade._id || `${trade.reportingCik}-${trade.transactionDate}-${Math.random().toString(36).substr(2, 9)}`,
                reportingName: trade.reportingName,
                typeOfOwner: trade.typeOfOwner,
                companySymbol: trade.symbol,
                securityName: trade.securityName,
                filingDate: new Date(trade.filingDate),
                transactionDate: new Date(trade.transactionDate),
                securitiesTransacted: trade.securitiesTransacted,
                price: trade.price,
                transactionType: trade.transactionType,
                acquisitionOrDisposition: trade.acquisitionOrDisposition
            }));

            setCachedData(prev => ({
                ...prev,
                [cacheKey]: {
                    ...(prev[cacheKey] || {}),
                    [page]: transformedData,
                    _total: total
                }
            }));

            setTotalRows(total);
            setInitialLoadDone(true);
            setError(null);
        } catch (error) {
            console.error('Error fetching insider trades data:', error);
            setError(error.message || 'Failed to fetch insider trades data');
        } finally {
            setLoading(false);
        }
    }, [getCacheKey]);

    useEffect(() => {
        fetchTradesData(page, debouncedSymbol, selectedTypes, selectedAD);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSymbol, selectedTypes, selectedAD]);

    const hasActiveFilters = symbolQuery.trim() !== '' || selectedTypes.length > 0 || selectedAD.length > 0;

    const clearFilters = () => {
        setSymbolQuery('');
        setSelectedTypes([]);
        setSelectedAD([]);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setDebouncedSymbol('');
        setPage(0);
    };

    // Get current data from cache
    const cacheKey = getCacheKey(debouncedSymbol, selectedTypes, selectedAD);
    const currentData = (cachedData[cacheKey] || {})[page] || [];

    // Format transaction type
    const formatTransactionType = (type) => {
        if (!type) return '';
        const parts = type.split('-');
        const code = parts[0]?.trim();
        const description = parts[1]?.trim();
        switch (code) {
            case 'P': return 'Buy';
            case 'S': return 'Sale';
            case 'A': return 'Award';
            case 'X': return 'Option Exercise';
            case 'O': return 'Option Exercise';
            case 'G': return 'Gift';
            case 'C': return 'Conversion';
            case 'M': return 'Exempt';
            case 'L': return 'Small Acquisition';
            case 'D': return 'Return';
            case 'J': return 'Other';
            case 'F': return 'In-Kind';
            case 'I': return 'Discretionary';
            case 'W': return 'Will/Inheritance';
            case 'Z': return 'Trust';
            case 'U': return 'Tender';
            case 'E': return 'Expire Short';
            case 'H': return 'Expire Long';
            default: break;
        }
        if (description) return description;
        const lowerType = type.toLowerCase();
        if (lowerType.includes('purchase') || lowerType.includes('buy')) return 'Buy';
        if (lowerType.includes('sale') || lowerType.includes('sell')) return 'Sale';
        if (lowerType.includes('option') || lowerType.includes('exercise')) return 'Option Exercise';
        if (lowerType.includes('proposed') || lowerType.includes('intent')) return 'Proposed Sale';
        return type;
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency: 'USD',
            minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(value);
    };

    const formatName = (name) => {
        if (!name) return '';
        return name.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const formatOwnerType = (ownerType) => {
        if (!ownerType) return '';
        if (ownerType.includes('See Remarks')) return '';
        if (ownerType.includes(':')) return ownerType.split(':')[1].trim();
        return ownerType;
    };

    const columns = [
        {
            field: 'insider',
            headerName: 'Insider',
            minWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <Box>
                    <Typography
                        sx={{
                            color: theme.palette.primary.main,
                            fontSize: '1rem',
                        }}
                    >
                        {formatName(params.row.reportingName)}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                        }}
                    >
                        {formatOwnerType(params.row.typeOfOwner)}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'symbol',
            headerName: 'Company',
            minWidth: 240,
            flex: 1,
            renderCell: (params) => (
                <Box>
                    <RouterLink
                        to={`/asset/${params.row.companySymbol}`}
                        style={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                        }}
                    >
                        {params.row.companySymbol}
                    </RouterLink>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                        }}
                    >
                        {params.row.securityName}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'transactionDate',
            headerName: 'Transaction Date',
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) => {
                return params.value.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            }
        },
        {
            field: 'filingDate',
            headerName: 'Filing Date',
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) => {
                return params.value.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            }
        },
        {
            field: 'price',
            headerName: 'Price',
            minWidth: 100,
            flex: 0.7,
            valueFormatter: (params) => formatCurrency(params.value)
        },
        {
            field: 'securitiesTransacted',
            headerName: 'Shares',
            minWidth: 100,
            flex: 0.7,
            valueFormatter: (params) => params.value.toLocaleString()
        },
        {
            field: 'value',
            headerName: 'Value',
            minWidth: 120,
            flex: 0.8,
            valueGetter: (params) => {
                return params.row.price * params.row.securitiesTransacted;
            },
            valueFormatter: (params) => formatCurrency(params.value)
        },
        {
            field: 'transactionType',
            headerName: 'Transaction',
            minWidth: 140,
            flex: 0.8,
            renderCell: (params) => {
                const transactionType = formatTransactionType(params.row.transactionType);
                let color;
                let bgColor;
                if (['Buy', 'Purchase', 'Award', 'Small Acquisition'].includes(transactionType)) {
                    color = '#fff'; bgColor = '#1e7e34';
                } else if (['Sale', 'Tender'].includes(transactionType)) {
                    color = '#fff'; bgColor = '#bd2130';
                } else if (['Option Exercise'].includes(transactionType)) {
                    color = '#fff'; bgColor = '#0056b3';
                } else if (['Proposed Sale'].includes(transactionType)) {
                    color = '#000'; bgColor = '#ffc107';
                } else if (['Gift', 'Will/Inheritance', 'Trust'].includes(transactionType)) {
                    color = '#fff'; bgColor = '#6c757d';
                } else if (['Conversion', 'Exempt', 'In-Kind', 'Discretionary', 'Other'].includes(transactionType)) {
                    color = '#000'; bgColor = '#e9ecef';
                } else {
                    color = '#fff'; bgColor = '#6610f2';
                }
                return (
                    <Typography
                        sx={{
                            color, backgroundColor: bgColor, fontWeight: 600,
                            borderRadius: '4px', padding: '4px 8px',
                            fontSize: '0.8125rem', display: 'inline-block',
                            textAlign: 'center', width: 'fit-content', minWidth: '90px'
                        }}
                    >
                        {transactionType}
                    </Typography>
                );
            }
        }
    ];

    const isInitialLoad = !initialLoadDone && loading;

    if (isInitialLoad) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {error}
            </Alert>
        );
    }

    return (
        <Container maxWidth="xl">
            <TitleAndInfo titleTop={titleTop} title={title} info={info} />

            {/* Filters Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                {/* Search */}
                <TextField
                    size="small"
                    placeholder="Search symbol or insider..."
                    value={symbolQuery}
                    onChange={(e) => setSymbolQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                        ...(symbolQuery && {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSymbolQuery('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }),
                    }}
                    sx={{
                        minWidth: 220,
                        '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' },
                    }}
                />

                {/* Transaction Type */}
                <TransactionTypeFilter
                    selectedTypes={selectedTypes}
                    setSelectedTypes={setSelectedTypes}
                    theme={theme}
                />

                {/* Acquisition / Disposition */}
                <AcquisitionDispositionFilter
                    selectedAD={selectedAD}
                    setSelectedAD={setSelectedAD}
                    theme={theme}
                />

                {/* Clear All */}
                {hasActiveFilters && (
                    <Button
                        size="small"
                        onClick={clearFilters}
                        startIcon={<CloseIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        Clear
                    </Button>
                )}
            </Box>

            <Box sx={{ height: 650, width: '100%' }}>
                <DataGrid
                    rows={currentData}
                    columns={columns}
                    paginationMode="server"
                    rowCount={totalRows}
                    pageSize={PAGE_SIZE}
                    paginationModel={{
                        page,
                        pageSize: PAGE_SIZE,
                    }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                    }}
                    loading={loading}
                    disableColumnMenu
                    hideFooterRowCount
                    hideFooterSelectedRowCount
                />
            </Box>
        </Container>
    );
};

export default InsiderTracker;